import { DEFAULT_LOCALE_CODE, LOCALE_MAP } from '@messenger/consts'
import {
  editorTextBoxStylesheet,
  ensureEditorTextBoxResolvedStyles,
  ensureEditorTextBoxStylesheet,
} from '@messenger/lotus'
import { EDITOR_TEXT_BOX_ITEM_TYPE } from '@messenger/types'
import {
  EDITOR_COLOR_PALETTE,
  themeSwatches,
  toEditorTextBoxThemeColors,
  type EditorTextBoxSwatch,
  type EditorTextBoxThemeColors,
} from './internal/editorTextBoxColorPalette'

export { EDITOR_TEXT_BOX_SEED_HEIGHT_PX } from './internal/editorTextBoxSeedHeight'

export { EDITOR_TEXT_BOX_SEED_TEXT } from '@messenger/lotus'

export { EDITOR_TEXT_BOX_ITEM_TYPE }

export const isEditorTextBoxItemType = (type: unknown): boolean =>
  type === EDITOR_TEXT_BOX_ITEM_TYPE

export const isEditorTextBoxItem = (item: unknown): boolean =>
  isEditorTextBoxItemType((item as { type?: unknown } | null | undefined)?.type)

/**
 * An editor item: a Lotus text box (a horizontal rule lives inside one as a
 * block, not as an item of its own). Every other page item (text, image,
 * line, QR code, watermark) is classic, and keeps its TinyMCE-era chrome and
 * resize gesture.
 */
export const isEditorItemType = (type: unknown): boolean =>
  isEditorTextBoxItemType(type)

export const isEditorItem = (item: unknown): boolean =>
  isEditorItemType((item as { type?: unknown } | null | undefined)?.type)

export const pageSetContentLocale = (pageSetLocale: unknown): string =>
  (typeof pageSetLocale === 'string' &&
    LOCALE_MAP[pageSetLocale as keyof typeof LOCALE_MAP]) ||
  DEFAULT_LOCALE_CODE

export const editorTextBoxSwatchRows = (
  themeColors: EditorTextBoxThemeColors | undefined,
): EditorTextBoxSwatch[] => [
  ...EDITOR_COLOR_PALETTE,
  ...themeSwatches(themeColors),
]

let structuralCss: string | null = null

// The floor and structure rules only. Every face comes from the app's own
// stylesheets, declared once at boot, so the face the editor warms at mount
// is the face that paints, and no Lotus sheet ever carries an @font-face.
export const ensureEditorTextBoxStructuralSheet = (): void => {
  if (structuralCss === null) structuralCss = editorTextBoxStylesheet()
  ensureEditorTextBoxStylesheet(structuralCss)
}

// Both Lotus sheets, the structural one and the append-only resolved one, go
// into the document before React renders anything. Each carries `@layer`
// rules, and Chromium rebuilds its whole font-face cache when a layered sheet
// first becomes active in the same style update as a rewrite of any sheet
// before it in the document — which is every React commit, since
// styled-components appends rules to its own sheet while components mount.
// Created at boot, in an update where nothing else changes, the sheets are
// plain appends; every face the editor warms afterwards stays loaded. Later
// calls to the two ensure functions find the sheets and change nothing.
export const installEditorTextBoxSheets = (): void => {
  ensureEditorTextBoxStructuralSheet()
  ensureEditorTextBoxResolvedStyles([])
}

export {
  editorPageContentFromWire,
  editorRoleDecorationInputs,
  editorTextBoxViewModels,
} from './internal/editorTextBoxBundle'
export type {
  EditorPageContentBundle,
  EditorRoleDecorationInputs,
  EditorTextBoxResolveInputs,
  EditorTextBoxWireRecord,
} from './internal/editorTextBoxBundle'

export {
  SERVICE_BLOCK_DATE_FORMAT,
  toEditorTextBoxCaseData,
  tabularCaseCells,
  fieldKeyToWritableCaseField,
  fieldKeyToDateColumn,
} from './internal/toEditorTextBoxCaseData'
export type { EditorTextBoxCaseContext } from './internal/toEditorTextBoxCaseData'
export {
  parseServiceAuthorColumn,
  serviceBlockPartKey,
  serviceDefaultIdKey,
  trimWrittenCaseValue,
} from './internal/fieldRules'
export type { ServiceAuthorTarget } from './internal/fieldRules'
export {
  TABULAR_FIELD_KEYS,
  additionalFamilyRowType,
  additionalFamilyRowTypes,
  caseColumnsWithTabularRows,
  isHonoraryPallbearerRow,
  tabularRowForm,
  tabularRowsFromCase,
} from './internal/tabularCaseRows'
export type {
  TabularRow,
  TabularRowForm,
  TabularRowInput,
} from './internal/tabularCaseRows'
export { toEditorTextBoxThemeColors }
export type { EditorTextBoxSwatch, EditorTextBoxThemeColors }

export { fieldLabel, namePartLabel } from './internal/fieldCatalog'
export { fieldMenuGroups } from './internal/fieldMenuGroups'
export type {
  FieldMenuGroup,
  FieldMenuLayout,
  FieldMenuRow,
} from './internal/fieldMenuGroups'
export {
  editorTextBoxColorName,
  editorTextBoxFieldGroupLabel,
  editorTextBoxFieldLabel,
  editorTextBoxFieldMenuLayoutLabel,
  editorTextBoxFieldMenuRowLabel,
  editorTextBoxFontRegionLabel,
  editorTextBoxI18nSlug,
  editorTextBoxNamePartLabel,
  editorTextBoxRoleLabel,
  editorTextBoxTableLabel,
} from './internal/editorTextBoxI18n'
export {
  nameOfDeceasedFromCase,
  nameOfDeceasedPartColumns,
} from './internal/nameOfDeceased'

export { PARAGRAPH_ROLE_LABELS, orderedRoles } from './internal/paragraphRoles'

export {
  EDITOR_FONT_FAMILIES,
  fontDisplayLabel,
} from './internal/editorTextBoxFontCatalog'
export {
  canonicalFontName,
  extractFontFamiliesFromHtml,
} from './internal/fontNames'
export { collectUsedFontsFromPages } from './internal/collectUsedFonts'

export {
  effectiveCharacterStyle,
  displayedTextCase,
  displayedLetterSpacingPt,
} from './internal/effectiveCharacterStyle'
export { characterStyleToCss } from './internal/characterStyleToCss'
export { pickLegibleBackdrop } from './internal/previewBackdrop'

export {
  EDITOR_EDIT_CHROME_SELECTOR,
  EDITOR_EDIT_CHROME_SELECTORS,
} from './internal/editorChrome'

export { formatPt } from './internal/ptFormat'
export { removeColorTarget } from './internal/editorTextBoxColorPalette'
export { cssColorToHex } from './internal/cssColorToHex'
export {
  MAX_TEXT_SIZE_GRID_PT,
  maxTextSizeOnCommit,
} from './internal/maxTextSizeField'
export {
  WCAG_CONTRAST_AA_NORMAL,
  contrastRatio,
  relativeLuminance,
} from './internal/colorContrast'

export {
  clampEditorAuthoredPt,
  clampEditorLetterSpacingPt,
  clampEditorStrokeWidthPt,
  editorFieldOutlinePaths,
  isEditorCaret,
} from '@messenger/lotus'
export type {
  EditorBoxLocalRect,
  EditorFieldOutline,
  EditorFieldOutlinePad,
  EditorResolvedClass,
} from '@messenger/lotus'

export { EDITOR_CONTENT_ROLE_VALUES } from '@messenger/lotus'
export type {
  EditorCharacterStylingInput,
  EditorContentRoleValue,
} from '@messenger/lotus/row-store'

export {
  renderEditorTextBox,
  editorTextBoxStylesheet,
  resolveEditorTextBoxDocument,
} from '@messenger/lotus'

export { editorTextBoxDocumentToInput } from '@messenger/lotus'

export {
  editorServiceTemplateContentFromBlocks,
  parseEditorServiceTemplateContent,
} from '@messenger/lotus'
export type { EditorServiceTemplateContent } from '@messenger/lotus'

export {
  editorAnchorPageId,
  editorTextBoxCurrentDocument,
  editorTextBoxDraftsAfterConflict,
  editorTextBoxGeometryDraftUpdates,
  editorTextBoxPendingDeletesAfterConflict,
  editorTextBoxWithItemGeometry,
} from './internal/editorTextBoxSave'
export type { EditorTextBoxItemLike } from './internal/editorTextBoxSave'
export { editorTextBoxPendingWrites } from './internal/editorTextBoxPendingWrites'

export {
  EDITOR_AUTOSIZE_MIN_PT,
  EDITOR_FIT_HEIGHT_TOL_PX,
  EDITOR_FONT_SETTLE_TIMEOUT_MS,
  editorBoxStateOf,
  editorFaceFailed,
  editorFaceInFlight,
  editorFaceLoading,
  editorFitWritesSizes,
  editorFrameHeightPx,
  editorHeldFrameHeight,
  editorTextBoxFacesLoaded,
  editorTextBoxFacesWriteSizes,
  editorTextBoxFailedFamilies,
  editorTextBoxFrameFollowsText,
  editorTextBoxLoadsSettled,
  editorTextGeometryHash,
  editorTextBoxLineHeightPx,
  fitRenderedEditorTextBox,
  installEditorBoxFaceRefit,
  installEditorFaceCoverage,
  installEditorFontRefitTrigger,
  installEditorFontRetry,
  isEditorCornerHandle,
  measureEditorGlyphGeometry,
  previewRenderedEditorTextBoxCornerScale,
  readEditorPaintedBox,
  readEditorPaintedTextBox,
  retryEditorFaces,
  settleEditorTextBoxFonts,
  EDITOR_BOX_TEXT_SIZING_COLUMNS,
  EDITOR_TEXT_SCALING_ENABLED,
  EDITOR_TEXT_SIZING_MODES,
  EDITOR_TEXT_SIZING_SHRINK,
  editorTextSizingOf,
} from '@messenger/lotus'
export type {
  EditorBoxState,
  EditorTextSizing,
  EditorTextSizingMode,
  EditorFontSizeReadout,
  EditorPaintedBlockStyle,
  EditorPaintedBoxStyle,
  EditorPaintedRunStyle,
  EditorPaintedTextBox,
  EditorResizeHandle,
} from '@messenger/lotus'
export type {
  EditorGeometryRect,
  EditorGlyphGeometry,
  EditorGlyphSource,
  EditorTextLineGeometry,
} from '@messenger/lotus'
export type {
  EditorFitResult,
  EditorInkClusterBox,
  EditorInkRect,
} from '@messenger/lotus'
export {
  EDITOR_INK_ESCAPE_ATTR,
  scaleEditorTextBoxDocumentSizes,
} from '@messenger/lotus'

export {
  EDITOR_TEXT_SHADOW_SEED,
  editorColorSpecFromCss,
  editorTextBoxStyleActions,
  mountEditorTextBox,
} from '@messenger/lotus'
export type {
  EditorTextBoxMount,
  EditorTextBoxMountOptions,
  EditorTextBoxToggleStates,
  EditorTextBoxToggleTriStates,
  EditorPosition,
  EditorSelection,
  EditorStylingAction,
  EditorTextShadow,
} from '@messenger/lotus'

export { ensureEditorTextBoxResolvedStyles } from '@messenger/lotus'

export {
  EDITOR_CASE_DETAIL_TABLE_BLOCK_CLASS_NAME,
  EDITOR_LITERAL_GRID_BLOCK_CLASS_NAME,
  EDITOR_RULE_BLOCK_CLASS_NAME,
  EDITOR_RULE_LINE_SELECTOR,
  EDITOR_BLOCK_ORDINAL_DATASET_KEY,
  EDITOR_EMOJI_CLUSTER_ATTR,
  EDITOR_EMPTY_FIELD_ATTR,
  EDITOR_EMPTY_FIELD_GHOST_CHAR,
  EDITOR_FILLER_ATTR,
  EDITOR_FIT_SCALE_DATASET_KEY,
  EDITOR_LEGACY_CHROME_ATTR,
  EDITOR_OVERFLOWING_ATTR,
  EDITOR_RUN_KIND_ATTR,
  EDITOR_RUN_ORDINAL_DATASET_KEY,
  EDITOR_SELECTION_HIGHLIGHT,
  EDITOR_TEXT_BLOCK_SELECTOR,
  EDITOR_TEXT_BOX_SELECTOR,
  EDITOR_TEXT_RUN_SELECTOR,
} from '@messenger/lotus'

export {
  EDITOR_DEFAULT_NAME_ORDER,
  EDITOR_FIELD_CATALOG,
  EDITOR_FIELD_FINAL_RESTING,
  EDITOR_FIELD_NAME_OF_DECEASED,
  EDITOR_NAME_PART_KEYS,
  editorCaseDataWithoutNameArrangement,
  editorCaseNameArrangement,
  editorCaseServices,
  editorServiceColumnKey,
  editorTableRowTypes,
  formatEditorMiddleInitial,
  isDefaultEditorNameParams,
  isEditorNamePart,
  resolveEditorCompositeName,
  resolveEditorField,
} from '@messenger/lotus'

export type {
  EditorBundledFont,
  EditorCaseData,
  EditorCaseService,
  EditorCharacterStyleLayer,
  EditorFieldCatalogEntry,
  EditorFieldParams,
  EditorFieldRunAddress,
  EditorNamePart,
  EditorRenderedTextBox,
  EditorResolvedBlock,
  EditorResolvedRun,
  EditorResolvedStyle,
  EditorResolvedTextBox,
  EditorSeveredBinding,
  EditorTextBoxDocument,
  RenderEditorTextBoxOptions,
} from '@messenger/lotus'

export { editorTextBoxViewModelResults } from './internal/editorTextBoxBundle'
export type { EditorTextBoxViewModelResult } from './internal/editorTextBoxBundle'

export {
  editorPageJsonItem,
  editorTextBoxItemGeometry,
  editorTextBoxItemStub,
  hydrateEditorTextBoxItems,
  isEmptyEditorTextBoxDocument,
  isPlaceholderOnlyEditorTextBoxDocument,
  sameEditorTextBoxContent,
} from './internal/editorTextBoxItems'
export type {
  EditorTextBoxItemGeometry,
  EditorTextBoxItemStub,
} from './internal/editorTextBoxItems'

export {
  EditorTextBoxConversionError,
  editorTextBoxFromTinyMceItem,
} from './internal/tinymce-conversion'
export type {
  EditorTextBoxConversion,
  EditorTextBoxConversionDroppedEdit,
  EditorTextBoxConversionErrorReason,
  EditorTextBoxConversionReport,
  EditorTextBoxConversionReportEntry,
  EditorTextBoxTinyMceItem,
} from './internal/tinymce-conversion'
export {
  EDITOR_MAX_BLOCKS_PER_BOX,
  EDITOR_MAX_RUN_CHARACTERS,
  EDITOR_MAX_RUNS_PER_BLOCK,
} from '@messenger/lotus'
export {
  editorPageSetTurnoverToastText,
  editorTextBoxConversionToastText,
  editorTextBoxListText,
} from './internal/editorTextBoxConversionToast'
export { editorPageSetIsTurnedOver } from './internal/editorPageSetTurnover'
export {
  editorPageSetHeightlessItems,
  editorPageSetPageId,
  editorPageSetTurnover,
  editorPageSetTurnoverCount,
} from './internal/editorPageSetTurnoverRun'
export type { EditorPageSetTurnoverHeight } from './internal/editorPageSetTurnoverRun'
export { measureEditorTurnoverTextHeights } from './internal/editorTurnoverTextHeights'
export type { EditorTurnoverTextItem } from './internal/editorTurnoverTextHeights'
export {
  editorCaseServiceIds,
  editorTextBoxConversionForCaseServices,
} from './internal/editorTextBoxConversionServiceBindings'

export { editorCharacterLayerFromStylingInput } from '@messenger/lotus'
export { editorCharacterStylingInputFromLayer } from './internal/editorTextBoxStylingInput'
export type {
  EditorDocumentContext,
  EditorDocumentRefs,
} from '@messenger/lotus'

export type {
  EditorBoxLayoutDescription,
  EditorFitExplanation,
  EditorFitState,
  EditorInspectBlockRole,
  EditorInspectLine,
} from '@messenger/lotus'

export {
  editorTextBlocksFromHtml,
  editorTextBoxFromHtmlInsert,
} from './internal/editorTextBoxHtmlInsert'
export type {
  EditorTextBlocksFromHtmlResult,
  EditorTextBoxHtmlInsert,
  EditorTextBoxHtmlInsertResult,
} from './internal/editorTextBoxHtmlInsert'
